// Reports: aging / bottleneck (per-stage time vs SLA), staff workload, and a date-ranged summary
// (received / completed / dispatched / cancelled / by type / doctor / clinic / staff / turnaround).
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { WfAgingReport, WfSummaryReport } from '@alignova/shared';
import { useCan } from '../../auth/useCan.ts';
import { PageHeader } from '../../ui/primitives.tsx';
import { WF, qs, useWfConfig, wfApi } from './workflowApi.ts';
import { Btn, Field, StagePill, StatTile, Tabs, hours, inputCls } from './wfUi.tsx';

const ymd = (d: Date) => d.toISOString().slice(0, 10);

export function WfReportsPage() {
  const [tab, setTab] = useState<'aging' | 'summary'>('aging');
  return (
    <div className="space-y-4">
      <PageHeader title="Reports" subtitle="Aging & bottlenecks · staff workload · period summary" />
      <Tabs value={tab} onChange={setTab} tabs={[{ key: 'aging', label: 'Aging / bottlenecks / workload' }, { key: 'summary', label: 'Period summary' }]} />
      {tab === 'aging' ? <Aging /> : <Summary />}
    </div>
  );
}

function Aging() {
  const cfg = useWfConfig();
  const can = useCan();
  const [r, setR] = useState<WfAgingReport | null>(null);
  useEffect(() => { wfApi.aging().then(setR).catch(() => undefined); }, []);
  if (!r) return <div className="text-muted">Loading…</div>;
  const maxAvg = Math.max(1, ...r.rows.map((x) => x.avgHours));
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        {r.bottleneckStage && <div className="rounded border border-amber-400 bg-amber-50 px-3 py-2 text-sm text-amber-900">Bottleneck stage right now: <b>{cfg.stageName(r.bottleneckStage)}</b></div>}
        <StatTile label="Stuck (over SLA)" value={r.stuckCases.length} tone={r.stuckCases.length ? 'bad' : 'good'} />
        {can('workflow:export') && <a className="ml-auto rounded border border-line px-3 py-1.5 text-sm hover:bg-surface-2" href={`/api${WF}/reports/aging.xlsx`}>Export Excel</a>}
      </div>
      <div className="overflow-x-auto rounded-lg border border-line bg-surface shadow-level-1">
        <table className="w-full text-sm">
          <thead className="bg-surface-2 text-left text-[11px] uppercase tracking-wider text-muted"><tr><th className="px-3 py-2">Stage</th><th>Active</th><th>Avg time in stage</th><th>Max</th><th>SLA</th><th>Stuck</th><th>Historical avg (completed visits)</th></tr></thead>
          <tbody>
            {r.rows.map((x) => (
              <tr key={x.stageKey} className="border-t border-line">
                <td className="px-3 py-1.5"><StagePill stageKey={x.stageKey} /></td>
                <td><Link className="font-mono hover:underline" to={`/workflow/cases?stage=${x.stageKey}`}>{x.activeCases}</Link></td>
                <td className="w-64"><div className="flex items-center gap-2"><div className="h-2 flex-1 rounded bg-surface-2"><div className={`h-2 rounded ${x.slaHours && x.avgHours > x.slaHours ? 'bg-status-urgent-fg' : 'bg-accent'}`} style={{ width: `${(x.avgHours / maxAvg) * 100}%` }} /></div><span className="w-12 font-mono text-xs">{hours(x.avgHours)}</span></div></td>
                <td className="font-mono text-xs">{hours(x.maxHours)}</td>
                <td className="font-mono text-xs">{x.slaHours ? `${x.slaHours}h` : '-'}</td>
                <td>{x.stuck > 0 ? <Link className="font-mono font-bold text-status-urgent-fg hover:underline" to={`/workflow/cases?stage=${x.stageKey}&stuck=1`}>{x.stuck}</Link> : <span className="text-muted">0</span>}</td>
                <td className="font-mono text-xs">{x.completedVisits ? `${hours(x.avgHistoricalHours)} (${x.completedVisits})` : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-line bg-surface p-3 shadow-level-1">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted">Staff workload</h3>
          <table className="w-full text-sm"><thead className="text-left text-[11px] uppercase text-muted"><tr><th>Staff</th><th>Open</th><th>Due today</th><th>Overdue</th></tr></thead><tbody>
            {r.workload.map((w) => <tr key={w.userId ?? 'un'} className="border-t border-line"><td className="py-1"><Link className="hover:underline" to={`/workflow/cases?assignedTo=${w.userId ?? 'unassigned'}`}>{w.name}</Link></td><td className="font-mono">{w.open}</td><td className="font-mono">{w.dueToday}</td><td className={`font-mono ${w.overdue ? 'font-bold text-status-urgent-fg' : ''}`}>{w.overdue}</td></tr>)}
          </tbody></table>
        </div>
        <div className="rounded-lg border border-line bg-surface p-3 shadow-level-1">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted">Cases stuck over SLA</h3>
          <ul className="max-h-96 space-y-1 overflow-y-auto text-sm">
            {r.stuckCases.map((s) => <li key={s.id} className="flex items-center justify-between gap-2 border-b border-line py-1"><Link to={`/workflow/cases/${s.id}`} className="font-semibold uppercase hover:underline">{s.patientName}</Link><span className="font-mono text-xs text-muted">{s.caseNumber}</span><StagePill stageKey={s.stageKey} small /><span className="font-mono text-xs text-status-urgent-fg">{hours(s.hoursInStage)} / {s.slaHours}h</span></li>)}
            {!r.stuckCases.length && <li className="text-muted">Nothing over SLA 🎉</li>}
          </ul>
        </div>
      </div>
    </div>
  );
}

function Summary() {
  const cfg = useWfConfig();
  const can = useCan();
  const [from, setFrom] = useState(() => ymd(new Date(Date.now() - 30 * 86400000)));
  const [to, setTo] = useState(() => ymd(new Date()));
  const [r, setR] = useState<WfSummaryReport | null>(null);
  const run = () => wfApi.summary(from, to).then(setR).catch(() => undefined);
  useEffect(() => { void run(); }, []);
  const Bucket = ({ title, rows, labeler }: { title: string; rows: Array<{ key: string; count: number }>; labeler?: (k: string) => string }) => (
    <div className="rounded-lg border border-line bg-surface p-3 shadow-level-1">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted">{title}</h3>
      <ul className="max-h-64 space-y-0.5 overflow-y-auto text-sm">{rows.slice(0, 40).map((x) => <li key={x.key} className="flex justify-between border-b border-line py-0.5"><span className="truncate">{labeler ? labeler(x.key) : x.key}</span><span className="font-mono">{x.count}</span></li>)}{!rows.length && <li className="text-muted">-</li>}</ul>
    </div>
  );
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-2">
        <Field label="From"><input type="date" className={inputCls} value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
        <Field label="To"><input type="date" className={inputCls} value={to} onChange={(e) => setTo(e.target.value)} /></Field>
        <Btn tone="accent" onClick={() => void run()}>Run</Btn>
        {can('workflow:export') && <a className="ml-auto rounded border border-line px-3 py-1.5 text-sm hover:bg-surface-2" href={`/api${WF}/reports/summary.xlsx${qs({ from, to })}`}>Export Excel</a>}
      </div>
      {r && (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
            <StatTile label="Received" value={r.received} tone="accent" />
            <StatTile label="Completed (dispatched)" value={r.completed} tone="good" />
            <StatTile label="Cancelled" value={r.cancelled} />
            <StatTile label="Placed on hold" value={r.onHold} />
            <StatTile label="Reprints" value={r.reprints} tone={r.reprints ? 'warn' : 'neutral'} />
            <StatTile label="Reworks" value={r.reworks} tone={r.reworks ? 'warn' : 'neutral'} />
            <StatTile label="Avg completion" value={r.avgCompletionHours != null ? hours(r.avgCompletionHours) : '-'} sub="received → dispatched" />
            <StatTile label="Avg approval turnaround" value={r.avgApprovalHours != null ? hours(r.avgApprovalHours) : '-'} />
            <StatTile label="Avg production turnaround" value={r.avgProductionHours != null ? hours(r.avgProductionHours) : '-'} />
            <StatTile label="Avg dispatch turnaround" value={r.avgDispatchHours != null ? hours(r.avgDispatchHours) : '-'} sub="ready → dispatched" />
            <StatTile label="Overdue now" value={r.overdue} tone={r.overdue ? 'bad' : 'neutral'} />
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            <Bucket title="By case type" rows={r.byCaseType} labeler={(k) => cfg.optionLabel('case_type', k)} />
            <Bucket title="By doctor / group" rows={r.byDoctor} />
            <Bucket title="By clinic" rows={r.byClinic} />
            <Bucket title="By staff (assigned)" rows={r.byStaff} />
            <Bucket title="By current stage" rows={r.byStage} labeler={(k) => cfg.stageName(k)} />
            <Bucket title="By received through" rows={r.byReceivedThrough} labeler={(k) => cfg.optionLabel('received_through', k)} />
          </div>
        </>
      )}
    </div>
  );
}
